// Shared state via localStorage for cross-module data sync simulation

export interface SafetyTask {
  id: string;
  userName: string;
  address: string;
  phone: string;
  type: string;
  scheduledTime: string;
  scheduledDate: string;
  status: "待安检" | "进行中" | "已完成" | "已取消";
  priority: "normal" | "urgent";
  inspectorId: string;
  inspectorName: string;
  riskLevel?: string;
  district?: string;
  assignedAt?: string;
}

export interface BillRecord {
  id: string;
  userId: string;
  month: string;
  gasUsed: number;
  amount: number;
  status: "已缴费" | "待缴费" | "逾期";
  dueDate: string;
  paidDate?: string;
}

export interface InstallProgress {
  id: string;
  userId: string;
  address: string;
  currentStage: number;
  stages: { name: string; status: "completed" | "active" | "pending"; date?: string }[];
  submitDate: string;
}

const SAFETY_TASKS_KEY = "gas_safety_tasks";
const BILLS_KEY = "gas_bills";
const INSTALL_KEY = "gas_install_progress";

// Default safety tasks (simulating backend data)
const defaultSafetyTasks: SafetyTask[] = [
  {
    id: "SC-2026-0401",
    userName: "张建国",
    address: "朝阳区望京街道1号楼3单元201",
    phone: "138****5678",
    type: "年度安检",
    scheduledTime: "09:00-11:00",
    scheduledDate: "2026-04-11",
    status: "待安检",
    priority: "normal",
    inspectorId: "I001",
    inspectorName: "王建军",
    riskLevel: "高",
    district: "朝阳区",
    assignedAt: "2026-04-10 08:30",
  },
  {
    id: "SC-2026-0402",
    userName: "李秀英",
    address: "海淀区中关村大街88号2单元502",
    phone: "139****1234",
    type: "新装安检",
    scheduledTime: "11:00-13:00",
    scheduledDate: "2026-04-11",
    status: "待安检",
    priority: "urgent",
    inspectorId: "I001",
    inspectorName: "王建军",
    riskLevel: "极高",
    district: "朝阳区",
    assignedAt: "2026-04-10 08:30",
  },
  {
    id: "SC-2026-0403",
    userName: "王大明",
    address: "丰台区南四环西路128号3单元301",
    phone: "137****9876",
    type: "整改复检",
    scheduledTime: "14:00-16:00",
    scheduledDate: "2026-04-11",
    status: "待安检",
    priority: "normal",
    inspectorId: "I001",
    inspectorName: "王建军",
    riskLevel: "中",
    district: "朝阳区",
    assignedAt: "2026-04-10 08:30",
  },
  {
    id: "SC-2026-0404",
    userName: "赵小红",
    address: "西城区西单北大街56号1单元101",
    phone: "136****5432",
    type: "年度安检",
    scheduledTime: "16:00-18:00",
    scheduledDate: "2026-04-10",
    status: "已完成",
    priority: "normal",
    inspectorId: "I001",
    inspectorName: "王建军",
    riskLevel: "低",
    district: "朝阳区",
    assignedAt: "2026-04-09 17:00",
  },
];

const defaultBills: BillRecord[] = [
  { id: "B2026-03", userId: "U001", month: "2026年3月", gasUsed: 28.5, amount: 85.5, status: "已缴费", dueDate: "2026-04-15", paidDate: "2026-04-02" },
  { id: "B2026-02", userId: "U001", month: "2026年2月", gasUsed: 32.1, amount: 96.3, status: "已缴费", dueDate: "2026-03-15", paidDate: "2026-03-08" },
  { id: "B2026-01", userId: "U001", month: "2026年1月", gasUsed: 45.8, amount: 137.4, status: "已缴费", dueDate: "2026-02-15", paidDate: "2026-02-10" },
  { id: "B2025-12", userId: "U001", month: "2025年12月", gasUsed: 52.3, amount: 156.9, status: "已缴费", dueDate: "2026-01-15", paidDate: "2026-01-12" },
];

const defaultInstall: InstallProgress[] = [
  {
    id: "INS-2026-0089",
    userId: "U001",
    address: "朝阳区望京街道1号楼3单元201",
    currentStage: 4,
    submitDate: "2026-03-15",
    stages: [
      { name: "资料提交", status: "completed", date: "2026-03-15" },
      { name: "资质审核", status: "completed", date: "2026-03-18" },
      { name: "现场勘察", status: "completed", date: "2026-03-22" },
      { name: "施工安装", status: "active", date: "2026-04-08" },
      { name: "竣工验收", status: "pending" },
      { name: "通气调试", status: "pending" },
      { name: "完成开通", status: "pending" },
    ],
  },
];

export function getSafetyTasks(): SafetyTask[] {
  try {
    const stored = localStorage.getItem(SAFETY_TASKS_KEY);
    if (stored) return JSON.parse(stored) as SafetyTask[];
  } catch {
    // ignore
  }
  localStorage.setItem(SAFETY_TASKS_KEY, JSON.stringify(defaultSafetyTasks));
  return defaultSafetyTasks;
}

export function saveSafetyTasks(tasks: SafetyTask[]): void {
  localStorage.setItem(SAFETY_TASKS_KEY, JSON.stringify(tasks));
}

export function updateTaskStatus(taskId: string, status: SafetyTask["status"]): void {
  const tasks = getSafetyTasks();
  const updated = tasks.map(t => t.id === taskId ? { ...t, status } : t);
  saveSafetyTasks(updated);
}

export function addBatchTasks(newTasks: SafetyTask[]): void {
  const existing = getSafetyTasks();
  const existingIds = new Set(existing.map(t => t.id));
  const toAdd = newTasks.filter(t => !existingIds.has(t.id));
  saveSafetyTasks([...existing, ...toAdd]);
}

export function getBills(): BillRecord[] {
  try {
    const stored = localStorage.getItem(BILLS_KEY);
    if (stored) return JSON.parse(stored) as BillRecord[];
  } catch {
    // ignore
  }
  localStorage.setItem(BILLS_KEY, JSON.stringify(defaultBills));
  return defaultBills;
}

export function payBill(billId: string): void {
  const bills = getBills();
  const updated = bills.map(b =>
    b.id === billId ? { ...b, status: "已缴费" as const, paidDate: new Date().toISOString().slice(0, 10) } : b
  );
  localStorage.setItem(BILLS_KEY, JSON.stringify(updated));
}

export function getInstallProgress(): InstallProgress[] {
  try {
    const stored = localStorage.getItem(INSTALL_KEY);
    if (stored) return JSON.parse(stored) as InstallProgress[];
  } catch {
    // ignore
  }
  localStorage.setItem(INSTALL_KEY, JSON.stringify(defaultInstall));
  return defaultInstall;
}
